import React, { useRef, useEffect } from 'react';
import { useAudio } from '../../contexts/AudioContext';

export function AudioVisualizer({ width = 300, height = 60, barCount = 32 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const fallbackBarsRef = useRef([]);
  const { isPlaying, volume, getFrequencyData } = useAudio();

  useEffect(() => {
    // Initialize fallback bars
    if (fallbackBarsRef.current.length === 0) {
      fallbackBarsRef.current = Array(barCount).fill(0).map(() => 0.05);
    }
  }, [barCount]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      const barWidth = (width / barCount) - 2;
      const gap = 2;
      
      // Check if we should animate
      const shouldAnimate = isPlaying && volume > 0;
      
      // Try to get real frequency data
      let frequencyData = null;
      if (shouldAnimate && getFrequencyData) {
        frequencyData = getFrequencyData();
      }
      
      const hasRealData = frequencyData && frequencyData.some(v => v > 10);

      for (let i = 0; i < barCount; i++) {
        let barHeight;
        
        if (hasRealData) {
          // Use real frequency data
          const dataIndex = Math.floor(i * (frequencyData.length / barCount));
          const value = frequencyData[dataIndex] || 0;
          barHeight = Math.max(4, (value / 255) * height * 0.9);
        } else if (shouldAnimate) {
          // Animated fallback
          const target = Math.random();
          fallbackBarsRef.current[i] += (target - fallbackBarsRef.current[i]) * 0.3;
          barHeight = Math.max(4, fallbackBarsRef.current[i] * height * 0.9);
        } else {
          // Static when paused/muted
          fallbackBarsRef.current[i] *= 0.92;
          if (fallbackBarsRef.current[i] < 0.05) fallbackBarsRef.current[i] = 0.05;
          barHeight = Math.max(4, fallbackBarsRef.current[i] * height * 0.9);
        }

        const x = i * (barWidth + gap);
        const y = height - barHeight;

        // Purple to green gradient
        const gradient = ctx.createLinearGradient(x, height, x, y);
        gradient.addColorStop(0, '#8B5CF6');
        gradient.addColorStop(1, '#22C55E');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, volume, getFrequencyData, width, height, barCount]);

  return (
    <canvas
      ref={canvasRef}
      style={{ 
        width: `${width}px`, 
        height: `${height}px`,
        opacity: (isPlaying && volume > 0) ? 1 : 0.4
      }}
      data-testid="audio-visualizer"
    />
  );
}
